<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Message from './Message.vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  /** Index of first message in this run within the full conversation list */
  startIndex: {
    type: Number,
    required: true,
  },
  currentUserId: {
    type: Number,
    required: true,
  },
  isEmailInbox: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({}),
  },
  getInReplyToMessage: {
    type: Function,
    required: true,
  },
  shouldGroupWithNext: {
    type: Function,
    required: true,
  },
  allMessages: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const expanded = ref(false);

/** More than 6 consecutive activities → show 3 + toggle + 3 */
const COLLAPSE_AT = 7;
const HEAD = 3;
const TAIL = 3;

const needsCollapse = computed(() => props.messages.length >= COLLAPSE_AT);

const hiddenCount = computed(() =>
  needsCollapse.value ? props.messages.length - (HEAD + TAIL) : 0
);

const headMessages = computed(() => props.messages.slice(0, HEAD));
const tailMessages = computed(() => props.messages.slice(-TAIL));

const moreEventsLabel = computed(() =>
  t('CONVERSATION.ACTIVITY_MORE_EVENTS', { count: hiddenCount.value })
);

const showLessLabel = computed(() => t('CONVERSATION.ACTIVITY_SHOW_LESS'));

const messageBindings = (msg, indexInRun) => ({
  ...msg,
  isEmailInbox: props.isEmailInbox,
  inReplyTo: props.getInReplyToMessage(msg),
  groupWithNext: props.shouldGroupWithNext(
    props.startIndex + indexInRun,
    props.allMessages
  ),
  inboxSupportsReplyTo: props.inboxSupportsReplyTo,
  currentUserId: props.currentUserId,
});
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-1">
    <template v-if="!needsCollapse">
      <Message
        v-for="(msg, i) in messages"
        :key="msg.id"
        v-bind="messageBindings(msg, i)"
        data-clarity-mask="True"
        @retry="emit('retry', msg)"
      />
    </template>
    <template v-else-if="expanded">
      <Message
        v-for="(msg, i) in messages"
        :key="msg.id"
        v-bind="messageBindings(msg, i)"
        data-clarity-mask="True"
        @retry="emit('retry', msg)"
      />
      <button
        type="button"
        class="mx-auto w-fit rounded-md px-2 py-0.5 text-xs font-medium text-n-brand hover:bg-n-alpha-2 hover:text-n-brand"
        @click="expanded = false"
      >
        {{ showLessLabel }}
      </button>
    </template>
    <template v-else>
      <Message
        v-for="(msg, i) in headMessages"
        :key="msg.id"
        v-bind="messageBindings(msg, i)"
        data-clarity-mask="True"
        @retry="emit('retry', msg)"
      />
      <button
        type="button"
        class="mx-auto w-fit rounded-md px-2 py-0.5 text-left text-xs font-medium text-n-brand hover:bg-n-alpha-2 hover:text-n-brand"
        @click="expanded = true"
      >
        {{ moreEventsLabel }}
      </button>
      <Message
        v-for="(msg, i) in tailMessages"
        :key="msg.id"
        v-bind="messageBindings(msg, messages.length - TAIL + i)"
        data-clarity-mask="True"
        @retry="emit('retry', msg)"
      />
    </template>
  </div>
</template>
